import React, {useCallback, useEffect, useState} from "react";
import {Button} from "primereact/button";
import {notificationApi} from "../../models/notification/notification-api";
import {
    currentEndpointHash,
    currentPushService,
    enablePush,
    lastPushError,
    pushSupported,
    registerPushWorker,
} from "../../system/push/push-client";

// "Why don't I get push on this device?" — every link of the chain, checked live (task 12 follow-up).

const PROVIDERS = [
    {match: /fcm\.googleapis\.com|android\.googleapis\.com/, key: "google", label: "Google (Chrome / اندروید)"},
    {match: /notify\.windows\.com/, key: "microsoft", label: "Microsoft (Edge)"},
    {match: /mozilla\.com/, key: "mozilla", label: "Mozilla (Firefox)"},
    {match: /push\.apple\.com/, key: "apple", label: "Apple (Safari / آیفون)"},
];
const providerOf = (host) => PROVIDERS.find((provider) => provider.match.test(String(host || ""))) || null;

const formatTime = (value) => {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleString("fa-IR", {month: "long", day: "numeric", hour: "2-digit", minute: "2-digit"});
    } catch (error) {
        return "—";
    }
};

const STATUS_TEXT = {sent: "ارسال شد", failed: "ناموفق", skipped: "ارسال نشد", pending: "در صف", delayed: "به تعویق (ساعات بدون مزاحمت)", sending: "در حال ارسال"};

function Check({state, title, detail}) {
    const icon = {ok: "pi-check-circle text-green-600", warn: "pi-exclamation-triangle text-orange-500", bad: "pi-times-circle text-red-500", wait: "pi-spin pi-spinner text-500"}[state];
    return (
        <li className="flex align-items-start gap-2 py-2 border-bottom-1 border-100">
            <i className={`pi ${icon} mt-1`}/>
            <span className="flex flex-column gap-1 min-w-0">
                <span className="text-xs font-semibold text-800">{title}</span>
                {detail && <span className="text-xs text-500 line-height-3">{detail}</span>}
            </span>
        </li>
    );
}

export default function PushDiagnostics({publicKey, onChanged, toast}) {
    const [state, setState] = useState(null);
    const [busy, setBusy] = useState(false);

    const check = useCallback(async () => {
        const supported = pushSupported();
        const permission = typeof Notification === "undefined" ? "unsupported" : Notification.permission;
        const registration = supported ? await registerPushWorker() : null;
        const [service, hash, server] = await Promise.all([
            supported ? currentPushService() : null,
            supported ? currentEndpointHash() : null,
            notificationApi.pushStatus().catch(() => null),
        ]);
        setState({supported, permission, worker: Boolean(registration?.active), service, hash, server, error: lastPushError()});
    }, []);

    useEffect(() => {
        check();
    }, [check]);

    const resubscribe = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await enablePush(publicKey, {fresh: true});
            toast("success", "این مرورگر دوباره ثبت شد. «ارسال تست» را بزنید.");
        } catch (error) {
            toast("error", error?.message === "push.service-unreachable"
                ? "مرورگر نتوانست به سرویس Push خود وصل شود. راهنمای پایین را ببینید."
                : "ثبت دوباره انجام نشد.");
        } finally {
            setBusy(false);
            await check();
            onChanged?.();
        }
    };

    if (!state) return <ul className="list-none p-0 m-0"><Check state="wait" title="در حال بررسی…"/></ul>;

    const provider = providerOf(state.service);
    const device = (state.server?.devices || []).find((item) => item.endpointHash && item.endpointHash === state.hash);
    const egress = provider ? state.server?.egress?.[provider.key] : null;
    const serviceError = String(state.error?.code || "").startsWith("push.service-unreachable");

    return (
        <div className="flex flex-column gap-3">
            <ul className="list-none p-0 m-0">
                <Check
                    state={state.supported ? "ok" : "bad"}
                    title="پشتیبانی مرورگر"
                    detail={state.supported ? null : "این مرورگر یا این حالت (مثلاً پنجره ناشناس یا آیفونِ نصب‌نشده) Push را پشتیبانی نمی‌کند."}
                />
                <Check
                    state={state.permission === "granted" ? "ok" : state.permission === "denied" ? "bad" : "warn"}
                    title={`اجازه اعلان: ${state.permission === "granted" ? "داده شده" : state.permission === "denied" ? "مسدود" : "پرسیده نشده"}`}
                    detail={state.permission === "denied"
                        ? "روی آیکون قفل کنار آدرس سایت بزنید، Notifications را روی Allow بگذارید و صفحه را دوباره باز کنید."
                        : state.permission === "default" ? "روی «فعال‌سازی» بزنید و در پنجره مرورگر «Allow» را انتخاب کنید." : null}
                />
                <Check state={state.worker ? "ok" : "bad"} title="سرویس اعلان زوفینو (Service Worker)" detail={state.worker ? null : "صفحه را یک بار کامل بارگذاری کنید (Ctrl+Shift+R)."}/>
                <Check
                    state={state.service ? "ok" : serviceError ? "bad" : "warn"}
                    title={state.service ? `اشتراک این مرورگر: ${provider?.label || state.service}` : "این مرورگر هنوز اشتراک Push ندارد"}
                    detail={serviceError
                        ? "مرورگر نتوانست به سرویس Push خودش وصل شود (Chrome به سرویس گوگل، Edge به مایکروسافت، Firefox به موزیلا). این معمولاً به‌خاطر محدودیت شبکه است: یک بار با شبکه دیگر امتحان کنید یا روی دسکتاپ از Edge یا Firefox استفاده کنید. تا وقتی یک تب زوفینو باز است، اعلان‌ها روی همین دستگاه نمایش داده می‌شوند."
                        : null}
                />
                {state.service && (
                    <Check
                        state={device?.status === "active" ? "ok" : "bad"}
                        title={device ? `ثبت روی سرور: ${device.status === "active" ? "فعال" : "منقضی"}` : "این مرورگر روی سرور ثبت نشده"}
                        detail={device
                            ? `آخرین ارسال موفق: ${formatTime(device.lastSuccessAt)}${device.lastFailureAt ? ` · آخرین خطا: ${formatTime(device.lastFailureAt)}` : ""}`
                            : "«ثبت دوباره این مرورگر» را بزنید."}
                    />
                )}
                {provider && (
                    <Check
                        state={egress?.reachable ? "ok" : egress ? "bad" : "wait"}
                        title={`دسترسی سرور زوفینو به ${provider.label}`}
                        detail={egress && !egress.reachable ? "سرور ما به سرویس Push این مرورگر دسترسی ندارد؛ لطفاً این صفحه را برای پشتیبانی بفرستید." : null}
                    />
                )}
            </ul>

            {!!(state.server?.deliveries || []).length && (
                <div>
                    <div className="text-xs font-bold mb-2">آخرین ارسال‌ها به دستگاه‌های شما</div>
                    <ul className="list-none p-0 m-0 text-xs">
                        {state.server.deliveries.slice(0, 5).map((delivery, index) => (
                            <li key={index} className="flex align-items-center justify-content-between gap-2 py-1 border-bottom-1 border-50">
                                <span className="white-space-nowrap overflow-hidden text-overflow-ellipsis text-700">{delivery.title || delivery.eventKey}</span>
                                <span className={`flex-shrink-0 ${delivery.status === "sent" ? "text-green-600" : delivery.status === "failed" ? "text-red-500" : "text-500"}`}>
                                    {STATUS_TEXT[delivery.status] || delivery.status}
                                    {(delivery.results || []).some((result) => result.statusCode) && ` (${delivery.results.map((result) => result.statusCode).filter(Boolean).join("، ")})`}
                                    {" · "}{formatTime(delivery.at)}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="surface-50 border-round-lg p-3 text-xs text-600 line-height-3">
                <strong className="block text-700 mb-1">تست «ارسال شد» ولی چیزی نمایش داده نشد؟</strong>
                <span className="block">• ویندوز: <bdi dir="ltr" className="english-font">Settings → System → Notifications</bdi> — اعلان‌های Chrome / Edge روشن و <bdi dir="ltr" className="english-font">Do not disturb / Focus assist</bdi> خاموش باشد.</span>
                <span className="block">• مک: <bdi dir="ltr" className="english-font">System Settings → Notifications → Chrome → Allow notifications</bdi> روشن باشد.</span>
                <span className="block">• برای دریافت وقتی Chrome بسته است: <bdi dir="ltr" className="english-font">Settings → System → Continue running background apps</bdi> روشن باشد.</span>
                <span className="block">• اندروید: اعلان‌های مرورگر در تنظیمات برنامه روشن باشد و صرفه‌جویی باتری مرورگر را محدود نکند.</span>
            </div>

            <div className="flex flex-wrap gap-2">
                <Button label="بررسی دوباره" icon="pi pi-refresh" size="small" text className="text-xs gap-1" onClick={check}/>
                {state.supported && state.permission === "granted" && (
                    <Button label="ثبت دوباره این مرورگر" icon="pi pi-sync" size="small" outlined className="text-xs gap-1" loading={busy} onClick={resubscribe}/>
                )}
            </div>
        </div>
    );
}
